import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, catchError, of, tap } from 'rxjs';
import { environment } from '@home/src/app/environments/environment';
import { Router } from '@angular/router';
import { jwtDecode } from 'jwt-decode';

import {
  saveToken,
  getStoredToken,
} from '@home/src/app/core/interceptors/fetch-client';

const STORAGE_KEY = 'fincomData';
const TOKEN_FLAG = 'tokenProcessed';

@Injectable({
  providedIn: 'root',
})
export class AuthenticationService {
  private dotnetWebApiBearerToken = 'dotnetbearerToken';
  private pythonWebApiBearerToken = 'pythonbearerToken';
  private readonly dotNetTokenExpiryKey = 'dotnetbearerToken_expiresAt';
  private readonly pythonTokenExpiryKey = 'pythonbearerToken_expiresAt';
  private userSessionData: any = null;
  private committeesSessionData: any[] = [];
  private isAuthCheckedSource = new BehaviorSubject<boolean>(false);
  isAuthChecked$ = this.isAuthCheckedSource.asObservable();
  private fincomDataSource = new BehaviorSubject<Record<string, any> | null>(
    null,
  );
  fincomData$ = this.fincomDataSource.asObservable();

  constructor(
    @Inject(PLATFORM_ID) private platformId: object,
    private router: Router,
    private http: HttpClient,
  ) {}

  setSessionData(user: any) {
    this.userSessionData = user;
  }

  decodeToken(token: string) {
    try {
      return jwtDecode<any>(token);
    } catch {
      return null;
    }
  }

  getSessionData() {
    return this.userSessionData;
  }

  setComitteesData(user: any) {
    this.committeesSessionData = user;
  }

  getComitteesData() {
    return this.committeesSessionData;
  }

  public isBrowser(): boolean {
    return isPlatformBrowser(this.platformId);
  }

  setDotNetWebApiBearerToken(token: string): void {
    if (this.isBrowser()) {
      try {
        sessionStorage.setItem(this.dotnetWebApiBearerToken, token);
      } catch (error) {
        console.error('Failed to set auth token:', error);
      }
    }
  }

  setPythonWebApiBearerToken(token: string): void {
    if (this.isBrowser()) {
      try {
        sessionStorage.setItem(this.pythonWebApiBearerToken, token);
      } catch (error) {
        console.error('Failed to set auth token:', error);
      }
    }
  }

  getDotNetWebApiAuthToken(): string | null {
    return this.isBrowser()
      ? sessionStorage.getItem(this.dotnetWebApiBearerToken)
      : null;
  }

  getPythonWebApiAuthToken(): string | null {
    return this.isBrowser()
      ? sessionStorage.getItem(this.pythonWebApiBearerToken)
      : null;
  }

  clearDotNetWebApiBearerToken(): void {
    if (this.isBrowser()) {
      try {
        sessionStorage.removeItem(this.dotnetWebApiBearerToken);
        sessionStorage.removeItem(this.pythonWebApiBearerToken);
        sessionStorage.removeItem(this.dotNetTokenExpiryKey);
        sessionStorage.removeItem(this.pythonTokenExpiryKey);
      } catch (_) {
        console.error('Failed to clear auth token');
      }
    }
  }

  completeAuthCheck() {
    this.isAuthCheckedSource.next(true);
  }

  checkAndRedirectIfNotAuthenticated(): void {
    this.http
      .get('/api/session')
      .pipe(
        tap((res: any) => {
          this.isAuthCheckedSource.next(true);
          console.log('✅ Authenticated user:', res.user);
        }),
        catchError(() => {
          console.warn('🔁 Redirecting to /api/login due to missing session');
          window.location.href = '/api/login';
          return of(null);
        }),
      )
      .subscribe();
  }

  private isTokenExpired(exp: number): boolean {
    const now = Math.floor(Date.now() / 1000);
    return now >= exp;
  }

  validateStoredClaims(): boolean {
    if (this.isBrowser() && window.location.hostname === 'localhost') {
      return true;
    }
    const data = this.getClaimsData();
    const exp = data?.['exp'];
    if (!exp) return false;

    try {
      return !this.isTokenExpired(exp);
    } catch (e) {
      console.error('Invalid token payload:', e);
      return false;
    }
  }

  setClaimsData(data: Record<string, any>) {
    this.fincomDataSource.next(data);
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  }

  getClaimsData(): Record<string, any> | null {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : this.fincomDataSource.value;
  }

  overWriteClaimsData(updatedFields: Partial<Record<string, any>>) {
    const currentData = this.getClaimsData() || {};
    const newData = { ...currentData, ...updatedFields };
    this.setClaimsData(newData);
  }

  public async handleTokenLogin(token: string): Promise<boolean> {
    try {
      const res = await fetch(`${environment.apiUrl}/token-login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ token }),
      });

      if (!res.ok) throw new Error('Token login failed');

      const { fincomData } = await res.json();
      this.setClaimsData(fincomData);

      const dotNetStored = await getStoredToken(this.dotnetWebApiBearerToken);
      const pythonStored = getStoredToken(this.pythonWebApiBearerToken);

      // Now this if condition will not execute , bec if the tokens are expired, again i am fetching fresh tokens,
      // anyway added this code for safe side and redirecting them to 20 group page.
      if (
        dotNetStored.token &&
        (await pythonStored).token &&
        !dotNetStored.azureTokenexpired &&
        !(await pythonStored).azureTokenexpired
      ) {
        window.location.href = environment.redirectGroupUrl;
        return false;
      }

      // fetch fresh tokens
      const [dotNetRes, pythonRes] = await Promise.all([
        fetch(`${environment.apiUrl}/azureDotNetAccessToken`, {
          method: 'GET',
          credentials: 'include',
        }),
        fetch(`${environment.apiUrl}/azurePythonAccessToken`, {
          method: 'GET',
          credentials: 'include',
        }),
      ]);

      if (!dotNetRes.ok || !pythonRes.ok) {
        console.error('Token fetch failed');
        return false;
      }

      const [dotNetData, pythonData] = await Promise.all([
        dotNetRes.json(),
        pythonRes.json(),
      ]);

      saveToken(
        this.dotnetWebApiBearerToken,
        dotNetData.access_token,
        dotNetData.expires_in,
      );
      saveToken(
        this.pythonWebApiBearerToken,
        pythonData.access_token,
        pythonData.expires_in,
      );

      sessionStorage.setItem(TOKEN_FLAG, 'true');
      window.history.replaceState(
        {},
        '',
        window.location.pathname +
          window.location.search
            .replace(/([?&])token=[^&]*(&?)/, '$1')
            .replace(/[?&]$/, ''),
      );

      this.completeAuthCheck();
      await this.router.navigate(['/login']);
      return false;
    } catch (error) {
      console.error('Token login failed:', error);
      this.completeAuthCheck();
      await this.router.navigate(['/login'], {
        queryParams: { error: 'invalid_token' },
      });
      return false;
    }
  }
}
